import React, { useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberedListIcon,
} from '@mui/icons-material';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  minHeight?: string;
  maxHeight?: string;
  placeholder?: string;
}

export interface RichTextEditorHandle {
  insertContent: (html: string) => void;
  insertText: (text: string) => void;
  getHTML: () => string;
}

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  ({ content, onChange, minHeight = '200px', maxHeight = '400px', placeholder = 'Text eingeben...' }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const isInternalUpdate = useRef(false);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      insertContent: (html: string) => {
        if (editorRef.current) {
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            if (editorRef.current.contains(range.commonAncestorContainer)) {
              range.deleteContents();
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = html;
              const frag = document.createDocumentFragment();
              while (tempDiv.firstChild) {
                frag.appendChild(tempDiv.firstChild);
              }
              range.insertNode(frag);
              selection.collapseToEnd();
            } else {
              editorRef.current.innerHTML += html;
            }
          } else {
            editorRef.current.innerHTML += html;
          }
          triggerChange();
        }
      },
      insertText: (text: string) => {
        if (editorRef.current) {
          document.execCommand('insertText', false, text);
          triggerChange();
        }
      },
      getHTML: () => {
        return editorRef.current?.innerHTML || '';
      },
    }));

    // Sync content from props to editor
    useEffect(() => {
      if (editorRef.current && !isInternalUpdate.current) {
        if (editorRef.current.innerHTML !== content) {
          editorRef.current.innerHTML = content;
        }
      }
      isInternalUpdate.current = false;
    }, [content]);

    const triggerChange = () => {
      if (editorRef.current) {
        isInternalUpdate.current = true;
        onChange(editorRef.current.innerHTML);
      }
    };

    const handleInput = () => {
      triggerChange();
    };

    const execCommand = (command: string, value?: string) => {
      document.execCommand(command, false, value);
      editorRef.current?.focus();
      triggerChange();
    };

    return (
      <Box sx={{ border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            p: 0.5,
            borderBottom: '1px solid #ccc',
            backgroundColor: '#f5f5f5',
          }}
        >
          <Tooltip title="Fett">
            <IconButton size="small" onClick={() => execCommand('bold')}>
              <BoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Kursiv">
            <IconButton size="small" onClick={() => execCommand('italic')}>
              <ItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Unterstrichen">
            <IconButton size="small" onClick={() => execCommand('underline')}>
              <UnderlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Aufzählung">
            <IconButton size="small" onClick={() => execCommand('insertUnorderedList')}>
              <BulletListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Nummerierung">
            <IconButton size="small" onClick={() => execCommand('insertOrderedList')}>
              <NumberedListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Editor */}
        <Box
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          sx={{
            minHeight,
            maxHeight,
            overflow: 'auto',
            p: 2,
            outline: 'none',
            '&:empty::before': {
              content: `"${placeholder}"`,
              color: '#999',
            },
            '& p': { margin: '0 0 8px 0' },
            '& ul, & ol': { marginLeft: '20px' },
          }}
        />
      </Box>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
